import { g, layers } from '@hcie/core';
import { EventBus, drawShapesToCtx, Shape } from '@hcie/shared';
import { getMaskBounds } from './mask-logic';

/**
 * @file transformer.ts
 * @description Manages moving and transforming selection content.
 */

export class SelectionTransformUI {
    public active: boolean = false;
    public overlay: HTMLDivElement | null = null;
    public controls: HTMLDivElement | null = null;
    public dragStartX: number = 0;
    public dragStartY: number = 0;
    public originalShapes: Shape[] | null = null;
    public originalLayerIndex: number = -1;
    public isDragging: boolean = false;
    public originalImageData: ImageData | null = null;

    constructor() {
        this.init();
    }

    init() {
        if (typeof document === 'undefined') return;
        this.overlay = document.createElement('div');
        this.overlay.id = 'selectionTransformOverlay';
        this.overlay.style.cssText = `
            position: absolute;
            border: 1px dashed #fff;
            box-shadow: 0 0 0 1px #000;
            pointer-events: none;
            display: none;
            z-index: 1000;
        `;

        this.controls = document.createElement('div');
        this.controls.style.cssText = `
            position: absolute;
            top: -35px;
            right: 0;
            display: flex;
            gap: 5px;
            pointer-events: auto;
        `;

        const dragHandle = document.createElement('div');
        dragHandle.innerHTML = '✛ Move';
        dragHandle.style.cssText = 'background: #333; color: #fff; padding: 4px 8px; font-size: 11px; cursor: move; border-radius: 3px; user-select: none;';
        dragHandle.addEventListener('mousedown', (e) => this.onDragStart(e));

        const okBtn = document.createElement('div');
        okBtn.innerHTML = '✓ OK';
        okBtn.style.cssText = 'background: #28a745; color: #fff; padding: 4px 10px; cursor: pointer; border-radius: 3px; font-size: 11px; font-weight: bold; user-select: none;';
        okBtn.onclick = (e) => { e.stopPropagation(); this.commit(); };

        const cancelBtn = document.createElement('div');
        cancelBtn.innerHTML = '✕ Cancel';
        cancelBtn.style.cssText = 'background: #dc3545; color: #fff; padding: 4px 10px; cursor: pointer; border-radius: 3px; font-size: 11px; font-weight: bold; user-select: none;';
        cancelBtn.onclick = (e) => { e.stopPropagation(); this.cancel(); };

        this.controls.appendChild(dragHandle);
        this.controls.appendChild(okBtn);
        this.controls.appendChild(cancelBtn);
        this.overlay.appendChild(this.controls);

        const wrapper = document.getElementById('canvasWrapper');
        if (wrapper) wrapper.appendChild(this.overlay);
    }

    onDragStart(e: MouseEvent) {
        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;

        const onMouseMove = (me: MouseEvent) => {
            if (!this.isDragging) return;
            const dx = (me.clientX - this.dragStartX) / g.zoom;
            const dy = (me.clientY - this.dragStartY) / g.zoom;
            moveSelectionContent(dx, dy);
            this.dragStartX = me.clientX;
            this.dragStartY = me.clientY;
        };

        const onMouseUp = () => {
            this.isDragging = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        e.stopPropagation();
        e.preventDefault();
    }

    show(bounds: { x: number, y: number, w: number, h: number }) {
        this.active = true;
        g.isTransforming = true;
        this.updatePosition(bounds);
        if (this.overlay) this.overlay.style.display = 'block';
    }

    updatePosition(bounds: { x: number, y: number, w: number, h: number }) {
        if (!this.overlay) return;
        this.overlay.style.left = (bounds.x * g.zoom) + 'px';
        this.overlay.style.top = (bounds.y * g.zoom) + 'px';
        this.overlay.style.width = (bounds.w * g.zoom) + 'px';
        this.overlay.style.height = (bounds.h * g.zoom) + 'px';
    }

    commit() {
        console.log("[DEBUG] Committing selection transform...");
        finalizeMoveContent();
        this.hide();
    }

    cancel() {
        console.log("[DEBUG] Cancelling selection transform...");
        if (floatingContent) {
            const layer = layers[this.originalLayerIndex];
            if (layer && this.originalImageData) {
                (layer.ctx as CanvasRenderingContext2D).putImageData(this.originalImageData, 0, 0);
                if (this.originalShapes) layer.shapes = this.originalShapes;
            }
        }
        floatingContent = null;
        g.floatingContent = null;
        this.hide();
        EventBus.emit('RENDER_LAYERS');
    }

    hide() {
        this.active = false;
        g.isTransforming = false;
        if (this.overlay) this.overlay.style.display = 'none';
        this.originalImageData = null;
        this.originalShapes = null;
    }
}

export let floatingContent: any = null;

export function moveSelectionContent(dx: number, dy: number) {
    if (!g.isSelectionActive || !g.selectionCanvas) return;

    if (!floatingContent) {
        const activeLayer = layers[g.activeLayerIndex];
        if (!activeLayer) return;

        const transformer = (window as any).transformUI as SelectionTransformUI;
        if (!transformer) return;

        transformer.originalShapes = activeLayer.shapes ? [...activeLayer.shapes] : [];
        transformer.originalImageData = (activeLayer.ctx as CanvasRenderingContext2D).getImageData(0, 0, activeLayer.canvas.width, activeLayer.canvas.height);
        transformer.originalLayerIndex = g.activeLayerIndex;

        if (activeLayer.shapes && activeLayer.shapes.length > 0) {
            drawShapesToCtx(activeLayer.ctx as CanvasRenderingContext2D, activeLayer.shapes);
            activeLayer.shapes = [];
        }

        const bounds = getMaskBounds(g.selectionCanvas as HTMLCanvasElement);
        if (!bounds) return;

        const fCanvas = g.createCanvas(bounds.w, bounds.h) as HTMLCanvasElement;
        const fctx = fCanvas.getContext("2d");

        const tempCanvas = g.createCanvas(g.image_width, g.image_height) as HTMLCanvasElement;
        const tctx = tempCanvas.getContext("2d");
        if (tctx && fctx) {
            tctx.drawImage(activeLayer.canvas as HTMLCanvasElement, 0, 0);
            tctx.globalCompositeOperation = "destination-in";
            tctx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
            fctx.drawImage(tempCanvas, -bounds.x, -bounds.y);

            const lctx = activeLayer.ctx as CanvasRenderingContext2D;
            lctx.save();
            lctx.globalCompositeOperation = "destination-out";
            lctx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
            lctx.restore();

            floatingContent = {
                canvas: fCanvas,
                x: bounds.x,
                y: bounds.y,
                originalLayerIndex: g.activeLayerIndex
            };

            transformer.show(bounds);
        }
    }

    if (floatingContent) {
        floatingContent.x += dx;
        floatingContent.y += dy;
        
        // This relies on moveSelectionMask being available. 
        // We'll expose it correctly in the index/facade or keep it here if appropriate.
        const detail = { dx, dy };
        EventBus.emit('MOVE_SELECTION_MASK', detail);

        const transformer = (window as any).transformUI as SelectionTransformUI;
        if (transformer) {
            transformer.updatePosition({
                x: floatingContent.x,
                y: floatingContent.y,
                w: floatingContent.canvas.width,
                h: floatingContent.canvas.height
            });
        }

        g.floatingContent = floatingContent;
    }
}

export function finalizeMoveContent() {
    if (!floatingContent) return;
    const activeLayer = layers[g.activeLayerIndex];
    if (activeLayer) {
        (activeLayer.ctx as CanvasRenderingContext2D).drawImage(floatingContent.canvas, floatingContent.x, floatingContent.y);
    }
    floatingContent = null;
    g.floatingContent = null;
    EventBus.emit('RENDER_LAYERS');
}
